import React, { memo, useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  CircularProgress,
  useTheme,
  IconButton
} from '@mui/material';
import {
  Home as HomeIcon,
  Hotel as BedIcon,
  Bathtub as BathtubIcon,
  Wifi as WifiIcon,
  AcUnit as AcUnitIcon,
  Kitchen as KitchenIcon,
  Shower as ShowerIcon,
  LocalLaundryService as WashingIcon,
  Hvac as HeatingIcon,
  Weekend as LivingIcon,
  KingBed as CoupleIcon,
  Kitchen as FridgeIcon,
  Microwave as MicrowaveIcon,
  Tv as TvIcon,
  DryCleaningOutlined as DryerIcon,
  LocalParking as ParkingIcon,
  Deck as GardenIcon,
  SmokeFree as NoSmokingIcon,
  CleaningServices as HooverIcon,
  Weekend as EntertainmentIcon,
  ChevronLeft,
  ChevronRight,
  Receipt
} from '@mui/icons-material';
import NoImageFound from '../NoImageFound';
import { priceFormattingSymbol } from '../../utils/utils';

// Add after existing imports
const getAvailabilityCount = (listing) => {
  const rooms = listing.rooms || [];
  let totalRooms = 0;
  let availableRooms = 0;

  rooms.forEach(room => {
    const bedsList = room.beds || [];
    totalRooms += bedsList.total;
    
    availableRooms += bedsList.available;
  });
  
  return {
    total: totalRooms,
    available: availableRooms,
    occupied: totalRooms - availableRooms
  };
};

const ListingCard = memo(({listing, selectedCustomer,...props}) =>{
    const theme = useTheme();
    
    const [imageState, setImageState] = useState({
      loading: true,
      error: false,
      cached: false
    });
    
    const imageCache = useRef(new Map());
    const isMounted = useRef(true);

    // Add state for carousel
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Add navigation handlers
    const handleNext = (e) => {
      e.stopPropagation();
      setCurrentImageIndex((prev) => 
        prev === (listing.images.length - 1) ? 0 : prev + 1
      );
    };
    
    const handlePrev = (e) => {
      e.stopPropagation();
      setCurrentImageIndex((prev) => 
        prev === 0 ? listing.images.length - 1 : prev - 1
      );
    };
  
    useEffect(() => {
      // Cleanup function to prevent memory leaks
      return () => {
        isMounted.current = false;
      };
    }, []);
  
    useEffect(() => {
      if (!listing.image) {
        setImageState({ loading: false, error: true, cached: false });
        return;
      }
  
      // Check cache first
      if (imageCache.current.has(listing.image)) {
        setImageState({
          loading: false,
          error: !imageCache.current.get(listing.image),
          cached: true
        });
        return;
      }
  
      // Load image
      const img = new Image();
      img.src = listing.image;
      
      img.onload = () => {
        if (isMounted.current) {
          imageCache.current.set(listing.image, true);
          setImageState({
            loading: false,
            error: false,
            cached: true
          });
        }
      };
  
      img.onerror = () => {
        if (isMounted.current) {
          imageCache.current.set(listing.image, false);
          setImageState({
            loading: false,
            error: true,
            cached: true
          });
        }
      };
    }, [listing.image]);
    return(
      <Card
      {...props}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        mb: 2,
        height: { xs: 'auto', md: 250 },
        position: 'relative',
        border: theme => `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 3,
          transition: 'box-shadow 0.3s ease-in-out'
        }
      }}
    >
      {/* Add Managing Type Chip */}
      
      <Box sx={{
        position: 'absolute',
        top: 12,
        right: 12,
        zIndex: 2
      }}>
        {listing?.isBillIncluded!==0&&<Chip
                    label={"Bills Included"}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(25, 118, 210, 0.9)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      boxShadow: 2,
                      '& .MuiChip-label': {
                        color: 'white'
                      },
                      mr:1
                    }}
          />}
        <Chip
          label={listing.managingType?.name==="Managing" ? "Managed by Autumnrooms" : listing.managingType?.name}
          color={listing.managingType?.code === 'one_time' ? 'info' : 'primary'}
          size="small"
          sx={{
            bgcolor: theme => listing.managingType?.code === 'one_time' 
              ? 'rgba(41, 182, 246, 0.9)' 
              : 'rgba(25, 118, 210, 0.9)',
            color: 'white',
            fontWeight: 600,
            fontSize: '0.75rem',
            boxShadow: 2,
            '& .MuiChip-label': {
              color: 'white'
            }
          }}
        />
      </Box>

      <Box
        sx={{
          width: { xs: '100%', md: '20%' },
          minWidth: { xs: '100%', md: '200px' },
          height: { xs: '200px', md: '220px' },
          overflow: 'hidden',
          borderRadius: { xs: '12px 12px 0 0', md: '12px' },
          position: 'relative',
          backgroundColor: theme.palette.action.hover,
        }}
      >
        {imageState.loading ? (
          <Box sx={{ 
            width: '100%', 
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <CircularProgress size={40} />
          </Box>
        ) : imageState.error ? (
          <Box sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <NoImageFound width="100%" height="100%" />
          </Box>
        ) : (
          <>
            <CardMedia
              component="img"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.3s ease'
              }}
              image={listing.images[currentImageIndex]?.url || listing.image}
              alt={listing.title}
            />
            
            {listing.images.length > 1 && (
              <>
                <IconButton
                  onClick={handlePrev}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255,255,255,0.8)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                    width: 32,
                    height: 32
                  }}
                >
                  <ChevronLeft />
                </IconButton>
                
                <IconButton
                  onClick={handleNext}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255,255,255,0.8)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                    width: 32,
                    height: 32
                  }}
                >
                  <ChevronRight />
                </IconButton>
      
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem'
                  }}
                >
                  {currentImageIndex + 1} / {listing.images.length}
                </Box>
              </>
            )}
          </>
        )}
        
      </Box>

      <CardContent 
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: { xs: 1.5, sm: 2 },
        '&:last-child': { pb: { xs: 2, sm: 2 } }
      }}
      >
        {/* Compatibility Score */}
        {selectedCustomer?.studentId && (
          <Box sx={{ mb: 1 }}>
            <Chip
              label="98% Compatible"
              color="success"
              size="small"
              sx={{
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            />
          </Box>
        )}

        {/* Title */}
        <Typography variant="h6" gutterBottom
        sx={{
          fontSize: { xs: '1rem', sm: '1.25rem' }
        }}
        >
          {listing.title}
        </Typography>

        {/* Property Details */}
        <Box sx={{ 
                display: 'flex', 
                gap: { xs: 1, sm: 2 }, 
                mb: 2, 
                mt: 1,
                flexWrap: 'wrap'
              }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {listing.type}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {(() => {
                const counts = getAvailabilityCount(listing);
                return (
                  <Chip 
                    label={`${counts.available}/${counts.total} Available`}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ height: 20 }}
                  />
                );
              })()}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BathtubIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {listing.baths} Bath
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Receipt sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
            {listing?.isBillIncluded !== 0 ? "Bills Included" : "Bills Not Included"}
            </Typography>
          </Box>
          
        </Box>

        {/* Amenities */}
        <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              mb: 2, 
              flexWrap: 'wrap',
              maxHeight: { xs: '80px', sm: '100px' },
              overflow: 'hidden'
            }}>
            {listing?.amenities?.map((amenity, index) => {
              const amenityConfig = {
                kitchen: { icon: <KitchenIcon />, label: "Kitchen" },
                shower: { icon: <ShowerIcon />, label: "Shower" },
                heating: { icon: <HeatingIcon />, label: "Heating" },
                ac: { icon: <AcUnitIcon />, label: "AC" },
                wifi: { icon: <WifiIcon />, label: "WiFi" },
                wmachine: { icon: <WashingIcon />, label: "Washing Machine" },
                dryer: { icon: <DryerIcon />, label: "Dryer" },
                living: { icon: <LivingIcon />, label: "Living Room" },
                couple: { icon: <CoupleIcon />, label: "Couple Friendly" },
                fridge: { icon: <FridgeIcon />, label: "Fridge" },
                micoven: { icon: <MicrowaveIcon />, label: "Microwave" },
                tv: { icon: <TvIcon />, label: "TV" },
                showerHalf: { icon: <ShowerIcon />, label: "Half Bath" },
                parking: { icon: <ParkingIcon />, label: "Parking" },
                ctop: { icon: <KitchenIcon />, label: "Cooktop" },
                hoover: { icon: <HooverIcon />, label: "Vacuum" },
                brgarden: { icon: <GardenIcon />, label: "Garden" },
                noSmoking: { icon: <NoSmokingIcon />, label: "No Smoking" },
                entertainment: { icon: <EntertainmentIcon />, label: "Entertainment" }
              };
          
              const config = amenityConfig[amenity];
              if (!config) return null;
          
              return (
                <Chip
                  key={index}
                  icon={config.icon}
                  label={config.label}
                  size="small"
                  variant="outlined"
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'divider',
                    '& .MuiChip-icon': {
                      fontSize: '1rem',
                      mr: 0.5
                    },
                    '& .MuiChip-label': {
                      px: 0.5,
                      fontSize: '0.75rem'
                    },
                    '&:hover': {
                      bgcolor: 'action.hover',
                      borderColor: 'primary.main'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                />
              );
            })}
          </Box>

        {/* Location and Price */}
        <Box
         sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'flex-end' },
          gap: { xs: 1, sm: 0 }
        }}
        
        >
          <Box>
            {listing.university&&<Typography variant="body2" color="text.secondary"
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}

            >
              Near To {listing.university}
            </Typography>}
            {/* {listing?.distance!==0&&<Typography variant="body2" color="text.secondary"
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}


            >
              {listing.distance} Km from campus
            </Typography>} */}
          </Box>
          <Typography variant="h6" color="primary" 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: { xs: '1.05rem', sm: '1.1rem' }
                }}
          >
          From {priceFormattingSymbol(listing?.price,listing?.country?.currencyCode)}/month
          </Typography>
        </Box>
      </CardContent>
    </Card>
    )
  }, (prevProps, nextProps) => {
    return prevProps.listing.id === nextProps.listing.id;
  })

export default ListingCard;